/**
 * Module « inscription » — l’essai en démonstration, libre-service.
 *
 * Trois écrans publics, aucun compte requis (il n’y en a pas encore) :
 *
 *   /inscription                       le formulaire (démonstration d’un théâtre)
 *   /inscription/evenement             ★ EVT-02 — le formulaire raccourci d’une soirée
 *   /inscription/verification?jeton=…  la page qu’ouvre le lien du courriel
 *   /inscription/attente/:id           confirmation et suivi de la préparation
 *
 * Ils sont montés **à la racine** du routeur, pas sous `/admin` : ils
 * s’adressent à quelqu’un qui n’a pas encore de théâtre, et une garde de
 * session n’aurait donc rien à garder.
 *
 * Chargement paresseux : ces pages ne concernent ni le personnel connecté ni
 * les abonnés, elles n’ont rien à faire dans le lot initial.
 */

import { lazy } from 'react'
import type { RouteObject } from 'react-router'

/* La garde n'est PAS paresseuse : elle est l'élément de route lui-même, et un
   `Suspense` de plus avant de savoir si la page existe ferait clignoter
   l'écran pour rien. Elle ne pèse qu'une requête et deux écrans de texte. */
import { GardeInscriptionOuverte } from './ouverture'

const PageInscription = lazy(() =>
  import('./PageInscription').then((m) => ({ default: m.PageInscription })),
)
const PageInscriptionEvenement = lazy(() =>
  import('./PageInscriptionEvenement').then((m) => ({ default: m.PageInscriptionEvenement })),
)
const PageVerification = lazy(() =>
  import('./PageVerification').then((m) => ({ default: m.PageVerification })),
)
const PageAttente = lazy(() => import('./PageAttente').then((m) => ({ default: m.PageAttente })))

/**
 * ★★ C-07 — LA GARDE D'OUVERTURE, AU NIVEAU DE LA ROUTE.
 *
 * Elle enveloppe les DEUX formulaires de dépôt, et eux seuls. Posée là plutôt
 * que dans chaque page, elle ne s'écrit qu'une fois, elle ne rend conditionnel
 * aucun des hooks des formulaires — et surtout les deux pages restent des
 * composants purs, testables sans client de requêtes.
 *
 * ⚠️ `verification` et `attente/:id` restent HORS de la garde, délibérément :
 *    quelqu'un dont la demande a été déposée pendant que le libre-service
 *    était ouvert doit pouvoir la confirmer et la suivre jusqu'au bout, même
 *    si l'on ferme entre-temps. Fermer, c'est cesser d'accepter de NOUVELLES
 *    demandes — pas abandonner celles qui sont en cours.
 */
export const routesInscription: RouteObject[] = [
  {
    element: <GardeInscriptionOuverte />,
    children: [
      { index: true, element: <PageInscription /> },
      /* ★ EVT-02 — déclarée AVANT les routes paramétriques : `evenement` est un
         segment littéral, et il ne doit jamais être avalé par `attente/:id`. */
      { path: 'evenement', element: <PageInscriptionEvenement /> },
    ],
  },
  { path: 'verification', element: <PageVerification /> },
  { path: 'attente/:id', element: <PageAttente /> },
]

export {
  CHEMIN_ATTENTE,
  CHEMIN_INSCRIPTION,
  CHEMIN_INSCRIPTION_EVENEMENT,
  CHEMIN_VERIFICATION,
  DELAI_CONSERVATION_EVENEMENT_JOURS,
  DUREE_ESSAI_JOURS,
} from './chemins'

export type {
  Disponibilite,
  EtatDemande,
  EvenementDemande,
  StatutDemande,
  TypeDemande,
} from './api'
